import { useEffect, useEffectEvent, useMemo, useState } from 'react'
import { HighchartsReactRefObject } from '@highcharts/react'
import { alpha, Theme } from '@mui/material'
import { SVGElement } from 'highcharts'
import { useSnackbar } from 'notistack'

import Api from '../../../../../utils/api'
import useTemperatures from '../../../../../utils/hooks/useTemperatures'

interface CustomTooltip extends SVGElement {
  isMouseOverTooltip: () => boolean
  isMouseOverPlotband: () => boolean
  setPlotbandHover: (hover: boolean) => void
}

interface ExtendedChart extends Highcharts.Chart {
  customTooltip?: CustomTooltip | null
}

export const DEFAULT_CHART_TIME_RANGE: ChartTimeRange = 'week'
export const DEFAULT_CHART_TIME_RANGE_INDEX = 1

export const rangeSelectorIndexMatrix: Record<ChartTimeRange, number> = {
  day: 0,
  week: 1,
  month: 2,
}

export const defaultDataProps = {
  title: {
    align: 'center',
  },
  rangeSelector: {
    selected: DEFAULT_CHART_TIME_RANGE_INDEX,
  },
  legend: {
    enabled: true,
  },
  tooltip: {
    shared: true,
  },
  plotOptions: {
    series: {
      marker: {
        enabled: true,
        radius: 4,
      },
      showInNavigator: true,
    },
  },
}

export const lambdaChannelMatrix: { [key: string]: string[] } = {
  rssi: ['cell_con_flag'],
  batt: ['batt_count'],
  hum: ['fws_alert_ind', 'hmd_alert_ind'],
  temp: ['fla_ind', 'fla_score', 'hat_alert_ind', 'refrigeration_alert_ind', 'anomaly_count', 'anomaly_ref'],
}

export const cleanData = ({
  channelPoints,
  channel,
  formatTemperature,
  includeEndDate = false,
}: {
  channelPoints: DeviceDataChannelPoint[]
  channel: ChannelType
  formatTemperature: (temp: number, rounding?: 'tenths' | 'integer', fromUnit?: string, showUnit?: boolean) => string | number
  includeEndDate?: boolean
}) => {
  const isDBm = channel.unit === 'dBm'
  const isCelsius = channel.unit === '°C'

  const channelData = channelPoints
    .map((point) => {
      const { date, endDate, value: rawValue } = point
      let value: unknown = rawValue

      if (typeof rawValue === 'number') {
        // Normalize numeric points for chart display: round numbers to the tenth, convert dBm to positive magnitude, and convert temperature units
        let numericValue = Math.round(rawValue * 10) / 10
        if (isDBm) numericValue = Math.abs(numericValue)
        value = isCelsius ? formatTemperature(numericValue, 'tenths', 'degF', false) : numericValue
      }

      const start = new Date(date).getTime()

      if (includeEndDate && endDate) {
        const end = new Date(endDate).getTime()
        return [start, { value, endDate: Number.isNaN(end) ? undefined : end }] as [number, unknown]
      }

      return [start, value] as [number, unknown]
    })
    .filter(([timestamp]) => !Number.isNaN(timestamp)) // Ensure valid timestamps
    .sort((a, b) => a[0] - b[0])

  if (includeEndDate) return channelData

  return channelData.filter((d, i, arr) => !((d[0] === arr[i + 1]?.[0] && d[1] === null) || (d[0] === arr[i - 1]?.[0] && d[1] === null)))
}

export const useFetchChartData = (deviceId: number, channels: ChannelType[], chart: string) => {
  const { enqueueSnackbar } = useSnackbar()
  const { formatTemperature } = useTemperatures()
  const [data, setData] = useState<IChartData[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // Memoize the channels to prevent unnecessary fetchData calls
  const stableChannels = JSON.stringify(channels)
  const memoizedChannels: ChannelType[] = useMemo(() => JSON.parse(stableChannels), [stableChannels])

  const fetchData = async (timeRange: ChartTimeRange = DEFAULT_CHART_TIME_RANGE) => {
    setIsLoading(true)
    const channelsList = memoizedChannels.map((c) => c.channelName)

    // Call the endpoint for each channel and combine results
    const responses = await Promise.all(
      channelsList.map(
        async (channel) => (await Api.get(`/api/v2/protect/devices/${deviceId}/data?channels=${channel}&range=${timeRange}`)) as DeviceDataResponse
      )
    )

    if (responses.some((response) => !response.ok)) {
      enqueueSnackbar('Failed to fetch data', { variant: 'error' })
      setIsLoading(false)
      return
    }

    const combinedChannels = responses.reduce<Record<string, DeviceDataChannelPoint[]>>((acc, response) => {
      Object.entries(response.body.channels).forEach(([channelName, points]) => {
        acc[channelName] = [...(acc[channelName] ?? []), ...points]
      })
      return acc
    }, {})

    const isBinaryChart = chart === 'leak' || chart === 'leakprobe'

    const newData: IChartData[] = memoizedChannels.map((channel) => ({
      channelName: channel.channelName,
      yAxisTitle: channel.yAxisTitle,
      seriesName: channel.seriesName,
      unit: channel.unit,
      data: cleanData({
        channelPoints: combinedChannels[channel.channelName] ?? [],
        channel,
        formatTemperature,
        includeEndDate: isBinaryChart,
      }),
    }))

    setData(newData)
    setIsLoading(false)
  }

  const fetchInitialData = useEffectEvent(fetchData)
  useEffect(() => {
    fetchInitialData()
  }, [chart, deviceId, memoizedChannels])

  return { data, isLoading, fetchData }
}

const getChartAlerts = ({ alerts, channels }: { alerts: IAlertHistory[]; channels: ChannelType[] }) => {
  const channelNames = new Set(channels.map((c) => c.channelName))
  return alerts.filter((a) => channelNames.has(a.channel) || [...channelNames].some((name) => lambdaChannelMatrix[name]?.includes(a.channel)))
}

export const getAlertPlotBands = ({
  alerts,
  channels,
  chartRef,
  theme,
  formatDateTime,
}: {
  alerts: IAlertHistory[]
  channels: ChannelType[]
  chartRef: React.RefObject<HighchartsReactRefObject | null>
  theme: Theme
  formatDateTime: (date: string | Date) => string
}) => {
  const chartAlerts = getChartAlerts({ alerts, channels })

  return chartAlerts.flatMap((a) => {
    const enterEvent = a.eventHistory.find((e) => e.name === 'Alert Reported')
    const exitEvent = a.eventHistory.find((e) => e.name === 'Alert Resolved')

    const enterDate = enterEvent?.date ?? 0
    const exitDate = exitEvent?.date ?? (a.status !== 'resolved' ? new Date().toJSON() : undefined)

    if (!exitDate) return [] // Skip invalid data

    return {
      color: a.status === 'resolved' ? theme.palette.action.selected : alpha(theme.palette.warning.light, 0.3),
      from: new Date(enterDate).getTime(),
      to: new Date(exitDate).getTime(),
      events: {
        mouseover(e: MouseEvent) {
          if (!chartRef.current) return
          const { chart } = chartRef.current as { chart: ExtendedChart }

          // Remove existing custom tooltip
          if (chart.customTooltip) {
            chart.customTooltip.destroy()
          }

          // Create custom tooltip using chart renderer
          const tooltipText = `${a.status === 'resolved' ? 'Resolved' : 'Active'} Alert<br />${a.name}<br />${formatDateTime(enterDate.toString())} - ${formatDateTime(exitDate.toString())}`

          // Calculate safe positioning to avoid chart edges
          const tooltipWidth = 250 // Estimated tooltip width
          const tooltipHeight = 100 // Estimated tooltip height
          const margin = 20 // Safety margin from edges

          let x = e.offsetX
          let y = e.offsetY - 80

          // Adjust horizontal position if too close to right edge
          if (x + tooltipWidth > chart.plotLeft + chart.plotWidth) {
            x = chart.plotLeft + chart.plotWidth - tooltipWidth - margin
          }

          // Adjust horizontal position if too close to left edge
          if (x < chart.plotLeft + margin) {
            x = chart.plotLeft + margin
          }

          // Adjust vertical position if too close to top edge
          if (y < chart.plotTop + margin) {
            y = e.offsetY + 20 // Show below mouse instead of above
          }

          // Adjust vertical position if too close to bottom edge
          if (y + tooltipHeight > chart.plotTop + chart.plotHeight) {
            y = chart.plotTop + chart.plotHeight - tooltipHeight - margin
          }

          const tooltip = chart.renderer
            .label(tooltipText, x, y, 'callout', undefined, undefined, true)
            .attr({
              fill: 'rgba(0, 0, 0, 0.8)',
              padding: 10,
              r: 5,
              zIndex: 10,
            })
            .css({
              color: 'white',
              fontSize: '12px',
              fontWeight: 'normal',
            })
            .add()

          // Add mouse tracking for both tooltip and plotband
          let isMouseOverTooltip = false
          let isMouseOverPlotband = true // Mouse is over plotband when this event fires

          tooltip.on('mouseenter', () => {
            isMouseOverTooltip = true
          })

          tooltip.on('mouseleave', () => {
            isMouseOverTooltip = false
            // Only destroy tooltip if mouse is not over plotband either
            setTimeout(() => {
              if (chart.customTooltip && !isMouseOverTooltip && !isMouseOverPlotband) {
                chart.customTooltip.destroy()
                chart.customTooltip = null
              }
            }, 200)
          })

          // Store reference to tooltip for cleanup with both hover states
          chart.customTooltip = tooltip as CustomTooltip
          chart.customTooltip.isMouseOverTooltip = () => isMouseOverTooltip
          chart.customTooltip.isMouseOverPlotband = () => isMouseOverPlotband
          chart.customTooltip.setPlotbandHover = (hover: boolean) => {
            isMouseOverPlotband = hover
          }
        },
        mouseout() {
          const { chart } = chartRef.current as { chart: ExtendedChart }
          if (chart.customTooltip) {
            // Mark that mouse left the plotband
            chart.customTooltip.setPlotbandHover?.(false)

            setTimeout(() => {
              // Only destroy if mouse is not over tooltip or plotband
              const isOverTooltip = chart.customTooltip?.isMouseOverTooltip?.()
              const isOverPlotband = chart.customTooltip?.isMouseOverPlotband?.()

              if (chart.customTooltip && !isOverTooltip && !isOverPlotband) {
                chart.customTooltip.destroy()
                chart.customTooltip = null
              }
            }, 200)
          }
        },
      },
    }
  })
}

export const getAlertPlotLines = ({ alerts, channels, theme }: { alerts: IAlertHistory[]; channels: ChannelType[]; theme: Theme }) => {
  const chartAlerts = getChartAlerts({ alerts, channels })

  return chartAlerts.flatMap((a) =>
    a.responseHistory
      .filter((r) => r.response === 'ack' || r.response === 'test')
      .map((r) => ({
        color: theme.palette.primary.dark,
        dashStyle: 'ShortDot',
        width: 2,
        zIndex: 4,
        value: new Date(r.date).getTime(),
        label: {
          text: r.response === 'ack' ? 'Acknowledged' : 'Ignored',
          align: 'right',
          verticalAlign: 'middle',
        },
      }))
  )
}

// Each point represents an active event interval returned by the endpoint (e.g. a leak detection).
// Points are stored as [startMs, { value, endDate }] tuples by cleanData when includeEndDate is true.
export const extractSequences = (arr: ChartDataType): SeriesDataObject[] =>
  arr.map(([x, rawValue]) => {
    const meta = rawValue as { endDate?: number }
    return { x, x2: typeof meta.endDate === 'number' ? meta.endDate : x, y: 0, visible: true }
  })

export const getChartChannels = ({
  chart,
  currentValues,
  nodeType,
  tempUnits,
}: {
  chart: string
  currentValues: Record<string, IChannelValueType>
  nodeType: string
  tempUnits: string
}) => {
  const channels: ChannelType[] = []

  if (chart === 'signal' && currentValues.cellrssi?.value !== undefined)
    channels.push({
      channelName: 'cellrssi',
      yAxisTitle: 'Signal Strength (Cell RSSI)',
      seriesName: 'Cell RSSI',
      unit: 'dBm',
    })

  if (chart === 'signal' && currentValues.wifirssi?.value !== undefined)
    channels.push({
      channelName: 'wifirssi',
      yAxisTitle: 'Signal Strength (WiFi RSSI)',
      seriesName: 'WiFi RSSI',
      unit: 'dBm',
    })

  if (chart === 'signal' && currentValues.cellrsrq?.value !== undefined)
    channels.push({
      channelName: 'cellrsrq',
      yAxisTitle: 'Signal Strength (Cell RSRP)',
      seriesName: 'Cell RSRP',
      unit: 'dBm',
    })

  if (chart === 'signal' && currentValues.rssi?.value !== undefined)
    channels.push({
      channelName: 'rssi',
      yAxisTitle: 'Signal Strength (RSSI)',
      seriesName: 'RSSI',
      unit: 'dBm',
    })

  if (chart === 'signal' && currentValues.snr?.value !== undefined && nodeType !== 'kerlink_wifi_gateway') {
    channels.push({
      channelName: 'snr',
      yAxisTitle: 'Signal Quality (SNR)',
      seriesName: 'SNR',
      unit: 'dB',
    })
  }

  if (chart === 'temp' && currentValues.temp)
    channels.push({
      channelName: 'temp',
      yAxisTitle: `Temperature (deg ${tempUnits})`,
      seriesName: 'Temperature',
      unit: `°${tempUnits}`,
    })

  if (chart === 'temp' && currentValues.probe)
    channels.push({
      channelName: 'probe',
      yAxisTitle: `Probe (deg ${tempUnits})`,
      seriesName: 'Probe',
      unit: `°${tempUnits}`,
    })

  if (chart === 'temp' && currentValues.probe_temp)
    channels.push({
      channelName: 'probe_temp',
      yAxisTitle: `Probe (deg ${tempUnits})`,
      seriesName: 'Probe',
      unit: `°${tempUnits}`,
    })

  if (chart === 'temp' && currentValues.hum)
    channels.push({
      channelName: 'hum',
      yAxisTitle: 'Humidity (%)',
      seriesName: 'Humidity',
      unit: '%',
    })

  if (chart === 'temp' && currentValues.outsidetemp)
    channels.push({
      channelName: 'outsidetemp',
      yAxisTitle: `Outside Temperature (deg ${tempUnits})`,
      seriesName: 'Outside Temperature',
      unit: `°${tempUnits}`,
    })

  if (chart === 'battery' && currentValues.battery)
    channels.push({
      channelName: 'battery',
      yAxisTitle: 'Battery (%)',
      seriesName: 'Battery',
      unit: '%',
    })

  if (chart === 'leak' && currentValues.leak)
    channels.push({
      channelName: 'leak',
      yAxisTitle: '',
      seriesName: 'Leak',
      unit: '',
    })

  if (chart === 'leakprobe' && currentValues.leak_probe)
    channels.push({
      channelName: 'leak_probe',
      yAxisTitle: '',
      seriesName: 'Leak Probe',
      unit: '',
    })

  if (chart === 'ammonia' && currentValues.nh3)
    channels.push({
      channelName: 'nh3',
      yAxisTitle: 'Ammonia (ppm)',
      seriesName: 'Ammonia',
      unit: 'ppm',
    })

  return channels
}

export const getCharts = ({ currentValues }: { currentValues: Record<string, IChannelValueType> }) => {
  const charts: string[] = []

  if (currentValues.temp || currentValues.probe || currentValues.probe_temp || currentValues.hum) charts.push('temp')
  if (currentValues.leak) charts.push('leak')
  if (currentValues.leak_probe) charts.push('leakprobe')
  if (currentValues.rssi || currentValues.snr || currentValues.rsrp || currentValues.cellrssi || currentValues.cellrsrq || currentValues.wifirssi)
    charts.push('signal')
  if (currentValues.battery) charts.push('battery')

  return charts
}

export const getChartTitle = (chart: string, currentValues: Record<string, IChannelValueType>) => {
  if (chart === 'temp') {
    return currentValues.temp && currentValues.hum ? 'Temperature & Humidity' : 'Temperature'
  }

  if (chart === 'leakprobe' && currentValues.leak) return 'Water (Probe)'
  if (chart === 'leak' || chart === 'leakprobe') return 'Water'

  const chartTitleMatrix: Record<string, string> = {
    signal: 'Signal',
    battery: 'Battery',
    ammonia: 'Ammonia',
  }

  return chartTitleMatrix[chart] ?? chart.charAt(0).toUpperCase() + chart.slice(1)
}
